// src/components/PreferenceOnboardingModal.tsx
/**
 * Beautiful, interactive preference onboarding modal
 * Shows only low-confidence sections for user review
 */

import React, { useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonIcon,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardSubtitle,
  IonCardContent,
  IonChip,
  IonLabel,
  IonProgressBar,
  IonText,
  IonGrid,
  IonRow,
  IonCol,
  IonItem,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonCheckbox,
  IonRange,
} from '@ionic/react';
import { close, checkmark, helpCircle, star, sparkles, arrowBack } from 'ionicons/icons';
import './PreferenceOnboardingModal.css';
import {
  UserPreferences,
  PreferenceData,
  PREFERENCE_SECTIONS,
  getConfidenceColor,
  getConfidenceLabelForEditing,
} from '../types/preferences.ts';
import PreferenceService from '../services/PreferenceService.ts';

interface Props {
  isOpen: boolean;
  onDismiss: () => void;
  preferences: UserPreferences;
  onComplete?: (updatedPreferences: UserPreferences) => void;
}

export const PreferenceOnboardingModal: React.FC<Props> = ({
  isOpen,
  onDismiss,
  preferences,
  onComplete,
}) => {
  console.log('🎨 [PREFERENCE MODAL] Rendering with props:', {
    isOpen,
    hasPreferences: !!preferences,
    preferences,
  });

  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [editedData, setEditedData] = useState<Partial<PreferenceData>>({});
  const [saving, setSaving] = useState(false);

  // Early return if preferences is null
  if (!preferences) {
    console.log('⚠️ [PREFERENCE MODAL] No preferences - returning null');
    return null;
  }

  // Get low confidence sections - at TOP LEVEL, not in metadata!
  // Filter out removed sections (work_style, career_goals) that backend might still return
  const allLowConfidenceSections = preferences.low_confidence_sections || [];
  const lowConfidenceSections = allLowConfidenceSections.filter(section => {
    const sectionKey = section.section;
    // Only include sections that exist in our PREFERENCE_SECTIONS config
    const hasConfig = PREFERENCE_SECTIONS.some(s => s.key === sectionKey);
    if (!hasConfig) {
      console.log(`⚠️ [PREFERENCE MODAL] Filtering out removed section: ${sectionKey}`);
    }
    return hasConfig;
  });

  console.log('🎨 [PREFERENCE MODAL] Low confidence sections:', lowConfidenceSections);
  console.log('🎨 [PREFERENCE MODAL] Number of sections:', lowConfidenceSections.length);
  console.log('🎨 [PREFERENCE MODAL] Full preferences object:', preferences);

  // If no low confidence sections (after filtering), don't show the modal
  if (lowConfidenceSections.length === 0) {
    console.log('⚠️ [PREFERENCE MODAL] No low confidence sections - should not show modal');
    return null;
  }

  const currentSection = lowConfidenceSections[currentSectionIndex];
  const currentSectionKey = currentSection?.section;
  const sectionConfig = currentSectionKey
    ? PREFERENCE_SECTIONS.find(s => s.key === currentSectionKey)
    : null;
  const currentConfidence = currentSection?.confidence || 0;

  console.log('🎨 [PREFERENCE MODAL] Current section:', {
    index: currentSectionIndex,
    section: currentSection,
    sectionKey: currentSectionKey,
    sectionConfig,
    confidence: currentConfidence,
  });

  const progress = (currentSectionIndex + 1) / lowConfidenceSections.length;

  // Handle next section
  const handleNext = async () => {
    if (currentSectionIndex < lowConfidenceSections.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
    } else {
      await handleComplete();
    }
  };

  // Handle skip
  const handleSkip = () => {
    if (currentSectionIndex < lowConfidenceSections.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
    } else {
      onDismiss();
    }
  };

  // Handle back
  const handleBack = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
    }
  };

  // Handle complete
  const handleComplete = async () => {
    if (Object.keys(editedData).length === 0) {
      onDismiss();
      return;
    }

    setSaving(true);
    try {
      const response = await PreferenceService.updatePreferences(editedData);
      // Backend returns PreferenceMergeResponse with updated_preference field
      onComplete?.(response.updated_preference);
      onDismiss();
    } catch (error) {
      console.error('Failed to save preferences:', error);
    } finally {
      setSaving(false);
    }
  };

  // Handle field change
  const handleFieldChange = (sectionKey: string, fieldKey: string, value: any) => {
    setEditedData((prev) => ({
      ...prev,
      [sectionKey]: {
        ...(prev[sectionKey] || {}),
        [fieldKey]: value,
      },
    }));
  };

  // Render field editor based on section
  const renderFieldEditor = () => {
    if (!currentSectionKey || !sectionConfig) return null;

    const sectionKey = currentSectionKey;
    const sectionData =
      preferences?.preference_data?.[sectionKey as keyof PreferenceData] || {};
    const editedSectionData = editedData[sectionKey as keyof PreferenceData] || {};

    switch (sectionKey) {
      case 'job':
        return renderJobEditor(sectionData, editedSectionData);
      case 'locations':
        return renderLocationEditor(sectionData, editedSectionData);
      case 'skills':
        return renderSkillsEditor(sectionData, editedSectionData);
      case 'compensation':
        return renderCompensationEditor(sectionData, editedSectionData);
      case 'industries':
        return renderIndustriesEditor(sectionData, editedSectionData);
      case 'company':
        return renderCompanyEditor(sectionData, editedSectionData);
      default:
        return null;
    }
  };

  // Industries editor
  const renderIndustriesEditor = (data: any, edited: any) => {
    // Industries is a top-level array, not nested
    const industries = Array.isArray(data) ? data : (data.industries || []);
    const editedIndustries = Array.isArray(edited) ? edited : (edited.industries || industries);

    return (
      <IonGrid>
        <IonRow>
          <IonCol size="12">
            <IonText color="medium" className="description-text">
              Select the industries and fields you're interested in working in.
            </IonText>
            <IonText color="medium" className="helper-text">
              (You can add custom industries in the full preferences page)
            </IonText>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol size="12">
            <div className="chip-group">
              {['Technology', 'Finance', 'Healthcare', 'Education', 'E-commerce', 'Gaming', 'AI/ML', 'Fintech', 'SaaS', 'B2B', 'B2C', 'Crypto/Web3'].map((industry) => (
                <IonChip
                  key={industry}
                  color={editedIndustries.includes(industry) ? 'primary' : undefined}
                  onClick={() => {
                    const newIndustries = editedIndustries.includes(industry)
                      ? editedIndustries.filter((i: string) => i !== industry)
                      : [...editedIndustries, industry];
                    handleFieldChange('industries', 'industries', newIndustries);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <IonLabel>{industry}</IonLabel>
                </IonChip>
              ))}
            </div>
          </IonCol>
        </IonRow>
      </IonGrid>
    );
  };

  // Job preferences editor
  const renderJobEditor = (data: any, edited: any) => {
    return (
      <IonGrid>
        <IonRow>
          <IonCol size="12">
            <IonItem lines="none" button={false}>
              <IonInput
                label="Job Family"
                labelPlacement="stacked"
                value={edited.family || data.family || ''}
                placeholder="e.g., Engineering (Software)"
                onIonInput={(e) =>
                  handleFieldChange('job', 'family', (e.target as HTMLIonInputElement).value)
                }
              />
            </IonItem>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol size="12">
            <IonItem lines="none">
              <IonLabel position="stacked">Seniority Level</IonLabel>
              <IonSelect
                value={edited.seniority || data.seniority || ''}
                placeholder="Select level"
                interface="action-sheet"
                onIonChange={(e) =>
                  handleFieldChange('job', 'seniority', e.detail.value)
                }
              >
                <IonSelectOption value="junior">Junior</IonSelectOption>
                <IonSelectOption value="mid">Mid-Level</IonSelectOption>
                <IonSelectOption value="senior">Senior</IonSelectOption>
                <IonSelectOption value="lead">Lead</IonSelectOption>
                <IonSelectOption value="staff">Staff</IonSelectOption>
                <IonSelectOption value="principal">Principal</IonSelectOption>
              </IonSelect>
            </IonItem>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol size="12">
            <IonItem lines="none">
              <IonLabel position="stacked">Search Status</IonLabel>
              <IonSelect
                value={edited.search_status || data.search_status || ''}
                placeholder="Select status"
                interface="action-sheet"
                onIonChange={(e) =>
                  handleFieldChange('job', 'search_status', e.detail.value)
                }
              >
                <IonSelectOption value="actively_looking">
                  Actively Looking
                </IonSelectOption>
                <IonSelectOption value="passively_looking">
                  Passively Looking
                </IonSelectOption>
                <IonSelectOption value="not_looking">Not Looking</IonSelectOption>
              </IonSelect>
            </IonItem>
          </IonCol>
        </IonRow>
      </IonGrid>
    );
  };

  // Location preferences editor
  const renderLocationEditor = (data: any, edited: any) => {
    return (
      <IonGrid>
        <IonRow>
          <IonCol size="12">
            <IonItem>
              <IonCheckbox
                checked={edited.remote !== undefined ? edited.remote : data.remote}
                onIonChange={(e) =>
                  handleFieldChange('locations', 'remote', e.detail.checked)
                }
              >
                Open to Remote Work
              </IonCheckbox>
            </IonItem>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol size="12">
            <IonItem>
              <IonCheckbox
                checked={
                  edited.hybrid_ok !== undefined ? edited.hybrid_ok : data.hybrid_ok
                }
                onIonChange={(e) =>
                  handleFieldChange('locations', 'hybrid_ok', e.detail.checked)
                }
              >
                Open to Hybrid Work
              </IonCheckbox>
            </IonItem>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol size="12">
            <IonItem>
              <IonCheckbox
                checked={
                  edited.relocation_willing !== undefined
                    ? edited.relocation_willing
                    : data.relocation_willing
                }
                onIonChange={(e) =>
                  handleFieldChange('locations', 'relocation_willing', e.detail.checked)
                }
              >
                Willing to Relocate
              </IonCheckbox>
            </IonItem>
          </IonCol>
        </IonRow>
      </IonGrid>
    );
  };

  // Skills editor
  const renderSkillsEditor = (data: any, edited: any) => {
    const skills = data.required || [];

    return (
      <IonGrid>
        <IonRow>
          <IonCol size="12">
            <IonText color="medium" className="description-text">
              We extracted <strong>{skills.length}</strong> skills from your resume.
              These will be used to match you with relevant opportunities.
            </IonText>
            <IonText color="medium" className="helper-text">
              (Informational only - you can add or modify skills in the full preferences page)
            </IonText>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol size="12">
            <div className="chip-group">
              {skills.length > 0 ? (
                skills.map((skill: string, index: number) => (
                  <IonChip key={index} className="read-only-chip">
                    <IonLabel>{skill}</IonLabel>
                  </IonChip>
                ))
              ) : (
                <IonText color="medium" className="empty-state-text">
                  No skills extracted. You can add them later in the full preferences page.
                </IonText>
              )}
            </div>
          </IonCol>
        </IonRow>
      </IonGrid>
    );
  };

  // Compensation editor
  const renderCompensationEditor = (data: any, edited: any) => {
    return (
      <IonGrid>
        <IonRow>
          <IonCol size="12">
            <IonItem lines="none" button={false}>
              <IonInput
                type="number"
                label="Minimum Salary (USD)"
                labelPlacement="stacked"
                value={edited.min || data.min || ''}
                placeholder="e.g., 100000"
                onIonInput={(e) =>
                  handleFieldChange('compensation', 'min', parseInt((e.target as HTMLIonInputElement).value as string || '0'))
                }
              />
            </IonItem>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol size="12">
            <IonItem lines="none" button={false}>
              <IonInput
                type="number"
                label="Maximum Salary (USD)"
                labelPlacement="stacked"
                value={edited.max || data.max || ''}
                placeholder="e.g., 150000"
                onIonInput={(e) =>
                  handleFieldChange('compensation', 'max', parseInt((e.target as HTMLIonInputElement).value as string || '0'))
                }
              />
            </IonItem>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol size="12">
            <IonItem lines="none">
              <IonCheckbox
                checked={
                  edited.equity_important !== undefined
                    ? edited.equity_important
                    : data.equity_important
                }
                onIonChange={(e) =>
                  handleFieldChange('compensation', 'equity_important', e.detail.checked)
                }
              >
                Equity is Important
              </IonCheckbox>
            </IonItem>
          </IonCol>
        </IonRow>
      </IonGrid>
    );
  };

  // Company preferences editor
  const renderCompanyEditor = (data: any, edited: any) => {
    // Merge edited and data for current state
    const currentStages = edited.stages !== undefined ? edited.stages : (data.stages || []);
    const currentSizes = edited.sizes !== undefined ? edited.sizes : (data.sizes || []);

    const handleStageToggle = (stage: string) => {
      const newStages = currentStages.includes(stage)
        ? currentStages.filter((s: string) => s !== stage)
        : [...currentStages, stage];
      handleFieldChange('company', 'stages', newStages);
    };

    const handleSizeToggle = (size: string) => {
      const newSizes = currentSizes.includes(size)
        ? currentSizes.filter((s: string) => s !== size)
        : [...currentSizes, size];
      handleFieldChange('company', 'sizes', newSizes);
    };

    return (
      <IonGrid>
        <IonRow>
          <IonCol size="12">
            <IonText color="medium" className="description-text">
              Select your preferred company characteristics:
            </IonText>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol size="12">
            <span className="section-label">Company Stage</span>
            <div className="chip-group">
              {['Seed', 'Series A-C', 'Unicorn', 'Public'].map((stage) => (
                <IonChip
                  key={stage}
                  color={currentStages.includes(stage) ? 'primary' : undefined}
                  onClick={() => handleStageToggle(stage)}
                  style={{ cursor: 'pointer' }}
                >
                  <IonLabel>{stage}</IonLabel>
                </IonChip>
              ))}
            </div>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol size="12">
            <span className="section-label">Company Size</span>
            <div className="chip-group">
              {['1-10', '11-50', '51-200', '200-1000', '1000+'].map((size) => (
                <IonChip
                  key={size}
                  color={currentSizes.includes(size) ? 'primary' : undefined}
                  onClick={() => handleSizeToggle(size)}
                  style={{ cursor: 'pointer' }}
                >
                  <IonLabel>{size}</IonLabel>
                </IonChip>
              ))}
            </div>
          </IonCol>
        </IonRow>
      </IonGrid>
    );
  };

  if (!sectionConfig) {
    console.error('❌ [PREFERENCE MODAL] No section config found for:', currentSectionKey);
    console.error('❌ [PREFERENCE MODAL] Available sections:', PREFERENCE_SECTIONS.map(s => s.key));
    return (
      <IonModal
        isOpen={isOpen}
        onDidDismiss={onDismiss}
        backdropDismiss={false}
        className="preference-onboarding-modal"
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>Error</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={onDismiss}>
                <IonIcon slot="icon-only" icon={close} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonText color="danger">
            <p>Could not find configuration for section: {currentSectionKey}</p>
            <p><small>Please contact support.</small></p>
          </IonText>
        </IonContent>
      </IonModal>
    );
  }

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onDismiss}
      backdropDismiss={false}
      className="preference-onboarding-modal"
    >
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            {currentSectionIndex > 0 && (
              <IonButton onClick={handleBack}>
                <IonIcon slot="icon-only" icon={arrowBack} />
              </IonButton>
            )}
          </IonButtons>
          <IonTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <IonIcon icon={sparkles} color="primary" />
              <span>Complete Your Profile</span>
            </div>
          </IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDismiss}>
              <IonIcon slot="icon-only" icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
        <IonProgressBar value={progress} color="primary" />
      </IonHeader>

      <IonContent className="ion-padding">
        <div className="onboarding-content">
          {/* Section card */}
          <IonCard className="section-card animated-card">
            <IonCardHeader>
              <div className="section-header">
                <div className="section-emoji">{sectionConfig.emoji}</div>
                <div>
                  <IonCardTitle>{sectionConfig.title}</IonCardTitle>
                  <IonCardSubtitle>{sectionConfig.description}</IonCardSubtitle>
                </div>
              </div>

              {/* Confidence indicator */}
              <div className="confidence-indicator">
                <IonChip color={getConfidenceColor(currentConfidence)} className="confidence-chip">
                  <IonIcon icon={helpCircle} />
                  <IonLabel>{getConfidenceLabelForEditing(currentConfidence)}</IonLabel>
                </IonChip>
                {!isNaN(currentConfidence) && (
                  <IonText color="medium" className="confidence-text">
                    <small>
                      We're {Math.round(currentConfidence * 100)}% confident about
                      this data
                    </small>
                  </IonText>
                )}
              </div>
            </IonCardHeader>

            <IonCardContent>{renderFieldEditor()}</IonCardContent>
          </IonCard>

          {/* Progress indicator + Action buttons */}
          <IonGrid style={{ padding: '0' }}>
            <IonRow className="ion-align-items-center ion-justify-content-between">
              {/* Progress text on the far left */}
              <IonCol size="auto" style={{ paddingLeft: '4px' }}>
                <IonText color="medium" className="progress-text">
                  <small style={{ fontSize: '12px', fontWeight: '600' }}>
                    Step {currentSectionIndex + 1} of {lowConfidenceSections.length}
                  </small>
                </IonText>
              </IonCol>

              {/* Skip and Next buttons on the far right */}
              <IonCol size="auto" style={{ display: 'flex', gap: '8px', paddingRight: '4px' }}>
                <IonButton fill="outline" onClick={handleSkip} style={{ minWidth: '75px' }}>
                  Skip
                </IonButton>
                <IonButton
                  onClick={handleNext}
                  disabled={saving}
                  color="primary"
                  style={{ minWidth: '85px' }}
                >
                  <IonIcon slot="start" icon={checkmark} />
                  {currentSectionIndex < lowConfidenceSections.length - 1
                    ? 'Next'
                    : 'Done'}
                </IonButton>
              </IonCol>
            </IonRow>
          </IonGrid>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default PreferenceOnboardingModal;
